import { Component, Input, OnDestroy, OnInit } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, loadedEmployeesTableConfig } from "src/app/store/reducers";
import {
  getEmployeeExcelList,
  getEmployeePartyList,
  resetCustomerEmployeesListState,
} from "src/app/store/actions/parties/parties.actions";

import { BaseEmployeesList } from "src/app/shared/base-classes/base-employees-list/base-employees-list";
import {
  ColumnSorterInfo,
  DefaultPage,
  ExportEvent,
  FilterEvent,
  FilterName,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import {
  removeColumnSorterInfoOptions,
  removeTableConfigOptions,
  setAdvancedFilter,
  setColumnInfoCategoriesProperties,
} from "src/app/shared/utils/tableUtils";

const USAGE_CODE = "CUSTOMER.EMPLOYEE";

@Component({
  selector: "customer-employees-list",
  templateUrl: "./customer-employees-list.component.html",
  styleUrls: ["./customer-employees-list.component.scss"],
})
export class CustomerEmployeesListComponent
  extends BaseEmployeesList
  implements OnInit, OnDestroy
{
  @Input() set code(code: string) {
    if (code) {
      this._code = code;
      this.getEmployeeCategoriesProperties(USAGE_CODE, this.getEmployeesList.bind(this));
    }
  }

  _code: string;

  constructor(store: Store<AppState>, private translate: TranslateService) {
    super(store);

    this.columnInfo$ = this.store.pipe(select(loadedEmployeesTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          columnInfo = removeColumnSorterInfoOptions(columnInfo);
          columnInfo = setColumnInfoCategoriesProperties(columnInfo, this.tableConfig);
          return columnInfo;
        }
      })
    );
  }

  ngOnInit(): void {
    this.setTranslatedTitle();
    this.tableConfig = removeTableConfigOptions(this.tableConfig);
  }

  setTranslatedTitle() {
    this.translate
      .get("Tabs.Employees")
      .subscribe((translatedTitle) => (this.tableTitle = translatedTitle));
  }

  getEmployeesList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getEmployeePartyList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  getEmployeesExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getEmployeeExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  setAdvancedFilter(filterEvent: FilterEvent) {
    const customerEmployeesFilter = {
      column: "customer.code",
      operator: FilterName.EQUALS_TO,
      value: this._code,
    };

    return setAdvancedFilter(customerEmployeesFilter, filterEvent);
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getEmployeesList.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getEmployeesExcelList.bind(this));
  }

  onSaveTableConfig(columnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(columnInfo, USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.loadedEmployeesSub) {
      this.loadedEmployeesSub.unsubscribe();
    }
    this.store.dispatch(resetCustomerEmployeesListState());
  }
}
